<template>
  <!-- eslint-disable vue/no-v-html -- setup copy is authored in setup_guides.js, never user input -->
  <section
    class="zone-card"
    data-testid="mcp-setup-steps"
  >
    <div
      class="app-card-row"
      data-testid="mcp-tool-picker"
    >
      <button
        v-for="guide in guides"
        :key="guide.key"
        type="button"
        class="app-card"
        :class="{ 'is-active': guide.key === selected }"
        :aria-pressed="guide.key === selected"
        :data-testid="`mcp-tool-card-${guide.key}`"
        @click="toggle(guide.key)"
      >
        <span class="app-card-main">
          <McpAppLogo :name="guide.key" />
          <b>{{ guide.label }}</b>
        </span>
        <span
          class="app-card-radio"
          aria-hidden="true"
        />
      </button>
    </div>

    <div
      v-if="activeGuide"
      class="setup-pane"
      data-testid="mcp-setup-pane"
    >
      <div
        v-if="activeGuide.note"
        class="zone-alert"
      >
        <div class="zone-alert-content">
          <div class="zone-alert-head">
            <i class="material-symbols-rounded">info</i>
            <b>{{ activeGuide.note.title }}</b>
          </div>
          <p v-html="activeGuide.note.text" />
        </div>
      </div>

      <div
        v-for="path in activeGuide.paths"
        :key="path.key"
        class="setup-path"
        :data-testid="`mcp-setup-path-${activeGuide.key}-${path.key}`"
      >
        <div class="setup-path-head">
          <i class="material-symbols-rounded">{{ path.icon }}</i>
          <b>{{ path.title }}</b>
        </div>

        <p v-if="path.lead">{{ path.lead }}</p>

        <ol
          v-if="path.steps"
          class="tool-steps"
        >
          <li
            v-for="(step, index) in path.steps"
            :key="index"
            v-html="step"
          />
        </ol>

        <div
          v-if="path.note"
          class="zone-alert"
        >
          <div class="zone-alert-content">
            <div class="zone-alert-head">
              <i class="material-symbols-rounded">groups</i>
              <b>{{ path.note.title }}</b>
            </div>
            <p v-html="path.note.text" />
          </div>
        </div>

        <McpCopyRow
          v-if="path.copy"
          :label="path.copy.label"
          :value="path.copy.value"
          :testid="`mcp-copy-${activeGuide.key}-${path.key}`"
        />

        <p
          v-if="path.key === 'app'"
          class="setup-foot"
        >
          Your Hubstaff password is never shared with the app. Once connected, it appears in
          <b>Connected AI apps</b> below.
        </p>
      </div>

      <div
        class="setup-path"
        data-testid="mcp-setup-test"
      >
        <div class="setup-path-head">
          <i class="material-symbols-rounded">chat</i>
          <b>Test it</b>
        </div>

        <p>Ask your AI tool a question about your Hubstaff data.</p>

        <McpCopyRow
          label="Test prompt"
          :value="starterPrompt"
          variant="outline"
          testid="mcp-copy-starter-prompt"
        />
      </div>
    </div>
  </section>
</template>

<script>
import McpAppLogo from '~/components/hubstaff_mcp/McpAppLogo.vue';
import McpCopyRow from '~/components/hubstaff_mcp/McpCopyRow.vue';
import setupGuides from '~/components/hubstaff_mcp/setup_guides';

export default {
  name: 'McpSetupSteps',
  components: { McpAppLogo, McpCopyRow },
  props: {
    mcpUrl: { type: String, required: true },
    starterPrompt: { type: String, required: true },
  },
  data() {
    return { selected: null };
  },
  computed: {
    guides() {
      return setupGuides(this.mcpUrl);
    },
    activeGuide() {
      return this.guides.find((guide) => guide.key === this.selected) || null;
    },
  },
  methods: {
    toggle(key) {
      this.selected = this.selected === key ? null : key;
    },
  },
};
</script>

<style scoped>
.zone-card {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  background: #fff;
  border: 1px solid var(--gray-200);
  border-radius: var(--rounded-lg);
}

.app-card-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.app-card {
  box-sizing: border-box;
  width: 200px;
  height: 80px;
  padding: 24px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid var(--gray-300);
  border-radius: var(--rounded-lg);
  background: #fff;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.app-card:hover {
  border-color: var(--gray-400);
}

.app-card.is-active {
  border: 2px solid var(--gray-900);
  padding: 23px 15px;
}

.app-card-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.app-card b {
  color: var(--gray-900);
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-card-radio {
  flex: none;
  width: 20px;
  height: 20px;
  border: 1px solid var(--gray-300);
  border-radius: 999px;
}

.app-card.is-active .app-card-radio {
  border: 6px solid var(--primary-700);
}

.setup-pane > .zone-alert {
  margin-bottom: 16px;
}

.setup-path + .setup-path {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--gray-200);
}

.setup-path-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.setup-path-head .material-symbols-rounded {
  font-size: 20px;
  color: var(--gray-900);
}

.setup-path-head b {
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: var(--gray-900);
}

.setup-path > p {
  margin: 0 0 12px;
  color: var(--gray-900);
  font-size: 14px;
  line-height: 20px;
}

.setup-path > .setup-foot {
  margin: 12px 0 0;
  color: var(--gray-500);
  font-size: 13px;
}

.setup-foot b {
  font-weight: 500;
}

.tool-steps {
  margin: 0;
  padding-left: 20px;
  list-style: decimal;
  color: var(--gray-900);
  font-size: 14px;
  line-height: 20px;
}

.tool-steps > li {
  margin-top: 8px;
  padding-left: 2px;
}

.tool-steps :deep(b) {
  display: inline;
  font-weight: 500;
}

.tool-steps + .zone-alert {
  margin-top: 16px;
}

.tool-steps + .zone-field {
  margin-top: 20px;
}

.zone-alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--primary-700);
  border-radius: var(--rounded-lg);
  background: var(--primary-50);
  overflow: clip;
}

.zone-alert-content {
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.zone-alert-head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.zone-alert-head .material-symbols-rounded {
  font-size: 18px;
  line-height: 16px;
  color: var(--primary-800);
}

.zone-alert-head b {
  flex: 1 0 0;
  min-width: 0;
  color: var(--primary-800);
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.zone-alert-content :deep(p) {
  margin: 0;
  color: var(--primary-800);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.zone-alert-content :deep(code) {
  color: var(--gray-700);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  background: var(--gray-100);
  border-radius: var(--rounded-sm);
  padding: 0 4px;
}
</style>
